// /apps/patient/src/pages/programs/ProgramsList.tsx
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Activity,
  Calendar,
  Clock,
  TrendingUp,
  CheckCircle,
  PlayCircle,
  BarChart,
  Target,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Award,
  Dumbbell,
  Check,
  Archive
} from 'lucide-react';
import { PageHeader } from '../../components/layout/PageHeader';
import { useFeatures } from '../../contexts/FeatureContext';
import { patientApiService } from '../../services/api';
import { format, parseISO, differenceInDays, addDays, startOfDay, isToday } from 'date-fns';

// Helper functions for day conversion
const jsToISODay = (jsDay: number): number => jsDay === 0 ? 7 : jsDay;
const isoDayToJS = (isoDay: number): number => isoDay === 7 ? 0 : isoDay;

function isEmbeddedVideo(url: string): boolean {
  if (!url) return false;
  try {
    const host = new URL(url).hostname.replace(/^www\./, '');
    return host === 'youtube.com'
      || host === 'm.youtube.com'
      || host === 'youtu.be'
      || host === 'vimeo.com'
      || host === 'player.vimeo.com';
  } catch {
    return false;
  }
}

function toEmbedUrl(url: string): string {
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, '');
    if (host === 'youtube.com' || host === 'm.youtube.com') {
      const v = u.searchParams.get('v');
      if (v) return `https://www.youtube.com/embed/${v}`;
    }
    if (host === 'youtu.be') {
      const id = u.pathname.slice(1);
      if (id) return `https://www.youtube.com/embed/${id}`;
    }
    if (host === 'vimeo.com') {
      const id = u.pathname.split('/').filter(Boolean)[0];
      if (id && /^\d+$/.test(id)) return `https://player.vimeo.com/video/${id}`;
    }
    return url;
  } catch {
    return url;
  }
}

interface ProgramAssignment {
  id: string;
  patient_id: string;
  provider_id: string;
  assignment_type: 'workout_plan' | 'rehab_program' | 'exercise_prescription';
  workout_plan_id?: string;
  rehab_program_id?: string;
  exercise_prescription_id?: string;
  start_date: string;
  end_date?: string;
  status: 'active' | 'completed' | 'paused' | 'discontinued';
  notes?: string;
  // Expanded program details
  program_details?: {
    name: string;
    description?: string;
    duration_weeks?: number;
    total_phases?: number;
    fitness_level?: string;
    condition?: string;
    protocol_source?: string;
  };
  // Progress information
  progress?: {
    current_week?: number;
    total_weeks?: number;
    current_phase?: number;
    total_phases?: number;
    sessions_completed: number;
    sessions_scheduled: number;
    completion_percentage: number;
    last_completed_date?: string;
    streak_days?: number;
  };
  schedule_info?: {
    scheduled_days: number[]; // ISO days (1=Mon, 7=Sun)
    frequency_per_week?: number;
    last_session_date?: string;
    next_session_date?: string;
  };
}

interface ProgramStats {
  totalActive: number;
  sessionsThisWeek: number;
  currentStreak: number;
  totalCompleted: number;
}

interface StandaloneExercise {
  id: string;
  exercise_id: string;
  label: string;
  value_json?: {
    sets?: string;
    reps?: string;
    resistance?: string;
    hold_time?: string;
    rest_time?: string;
    instructions?: string;
    description?: string;
    gif_url?: string;
    video_url?: string;
  } | null;
  is_active: boolean;
  recorded_at: number;
  created_at: number;
}

interface ExerciseCompletionRecord {
  id: string;
  exercise_id: string;
  exercise_name?: string;
  completed_at: number;
  sets_completed?: number;
  reps_completed?: string[];
}

function StandaloneExercisesView() {
  const [exercises, setExercises] = useState<StandaloneExercise[]>([]);
  const [completions, setCompletions] = useState<ExerciseCompletionRecord[]>([]);
  const [defaultsMap, setDefaultsMap] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [completionLoading, setCompletionLoading] = useState<string | null>(null);

  const activeExercises = exercises.filter(e => e.is_active);
  const archivedExercises = exercises.filter(e => !e.is_active);

  const today = format(new Date(), 'yyyy-MM-dd');
  const completedTodayIds = new Set(
    completions
      .filter(c => {
        const d = new Date(c.completed_at);
        const dateStr = format(d, 'yyyy-MM-dd');
        return dateStr === today;
      })
      .map(c => c.exercise_id)
  );
  if (completions.length > 0) {
    console.log('[StandaloneExercises] today:', today, 'completions:', completions.length, 'completedTodayIds:', [...completedTodayIds]);
    console.log('[StandaloneExercises] exercise_ids in list:', exercises.filter(e => e.is_active).map(e => e.exercise_id));
  }

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [allExercises, todayCompletions] = await Promise.all([
        patientApiService.getStandaloneExercises(),
        patientApiService.getStandaloneExerciseCompletions({
          start_date: today + 'T00:00:00',
          end_date: today + 'T23:59:59',
        }),
      ]);
      const exerciseList = Array.isArray(allExercises) ? allExercises : allExercises?.items || [];
      setExercises(exerciseList);
      const completionList = Array.isArray(todayCompletions) ? todayCompletions : todayCompletions?.items || [];
      console.log('[StandaloneExercises] completions response:', todayCompletions, '→ parsed:', completionList);
      setCompletions(completionList);

      const ids = exerciseList.map((e: any) => e.exercise_id).filter(Boolean);
      if (ids.length > 0) {
        try {
          const defaults = await patientApiService.getExerciseDefaults(ids.join(','));
          const dList = Array.isArray(defaults) ? defaults : defaults?.items || [];
          const map: Record<string, any> = {};
          dList.forEach((d: any) => { if (d.id && d.defaults) map[d.id] = d.defaults; });
          setDefaultsMap(map);
        } catch { /* non-blocking */ }
      }
    } catch (err) {
      console.error('Failed to fetch exercises:', err);
    } finally {
      setLoading(false);
    }
  }, [today]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleComplete = async (exercise: StandaloneExercise, setsCompleted?: number, repsCompleted?: string[]) => {
    if (completedTodayIds.has(exercise.exercise_id)) return;
    setCompletionLoading(exercise.id);
    try {
      const completion: any = {
        exercise_id: exercise.exercise_id,
        exercise_name: exercise.label,
      };
      if (setsCompleted) completion.sets_completed = setsCompleted;
      if (repsCompleted?.length) completion.reps_completed = repsCompleted;

      await patientApiService.completeStandaloneExercises([completion]);
      setCompletions(prev => [...prev, {
        id: 'temp-' + Date.now(),
        exercise_id: exercise.exercise_id,
        exercise_name: exercise.label,
        completed_at: Date.now(),
        sets_completed: setsCompleted,
        reps_completed: repsCompleted,
      }]);
    } catch (err) {
      console.error('Failed to log completion:', err);
    } finally {
      setCompletionLoading(null);
    }
  };

  const getLastCompleted = (exerciseId: string): string | null => {
    const match = completions.find(c => c.exercise_id === exerciseId);
    if (match) return format(new Date(match.completed_at), 'MMM d');
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const completedCount = activeExercises.filter(e => completedTodayIds.has(e.exercise_id)).length;

  return (
    <div className="space-y-6">
      {/* Daily progress */}
      {activeExercises.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-semibold text-gray-900">Today</h3>
            <span className="text-sm text-gray-500">
              {completedCount}/{activeExercises.length} completed
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
            <div
              className="bg-primary h-2 rounded-full transition-all"
              style={{ width: `${activeExercises.length > 0 ? (completedCount / activeExercises.length) * 100 : 0}%` }}
            />
          </div>
        </div>
      )}

      {/* Active exercises */}
      {activeExercises.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm p-12 text-center">
          <Dumbbell className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No exercises assigned</h3>
          <p className="text-gray-600">No individual exercises have been added yet.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {activeExercises.map(exercise => {
            const isDone = completedTodayIds.has(exercise.exercise_id);
            const isExpanded = expandedId === exercise.id;
            const vj = exercise.value_json || {};
            const defs = defaultsMap[exercise.exercise_id] || {};
            const params: string[] = [];
            if (vj.sets || defs.sets) params.push(`${vj.sets || defs.sets} sets`);
            if (vj.reps || defs.reps) params.push(`${vj.reps || defs.reps} reps`);
            if (vj.resistance || defs.resistance) params.push(vj.resistance || defs.resistance);
            if (vj.hold_time || defs.hold_time) params.push(`${vj.hold_time || defs.hold_time}s hold`);
            const lastCompleted = getLastCompleted(exercise.exercise_id);

            return (
              <div key={exercise.id} className="bg-white rounded-lg shadow-sm overflow-hidden">
                <div className="flex items-center p-4 gap-3">
                  {/* Checkbox */}
                  <button
                    type="button"
                    disabled={isDone || completionLoading === exercise.id}
                    onClick={(e) => { e.stopPropagation(); handleComplete(exercise); }}
                    className={`flex-shrink-0 w-8 h-8 rounded-full border-2 flex items-center justify-center transition-colors ${
                      isDone
                        ? 'bg-green-500 border-green-500 text-white'
                        : completionLoading === exercise.id
                        ? 'border-gray-300 animate-pulse'
                        : 'border-gray-300 hover:border-primary'
                    }`}
                  >
                    {isDone && <Check className="h-4 w-4" />}
                  </button>

                  {/* Thumbnail */}
                  {vj.gif_url ? (
                    <img src={vj.gif_url} alt="" className="h-12 w-12 rounded-lg object-cover flex-shrink-0" />
                  ) : (
                    <div className="h-12 w-12 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                      <Dumbbell className="h-5 w-5 text-gray-400" />
                    </div>
                  )}

                  {/* Info */}
                  <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setExpandedId(isExpanded ? null : exercise.id)}>
                    <p className={`text-sm font-medium truncate ${isDone ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
                      {exercise.label}
                    </p>
                    {params.length > 0 && (
                      <p className="text-xs text-gray-500 mt-0.5">{params.join(' · ')}</p>
                    )}
                    {lastCompleted && !isDone && (
                      <p className="text-xs text-gray-400 mt-0.5">Last completed: {lastCompleted}</p>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={() => setExpandedId(isExpanded ? null : exercise.id)}
                    className="flex-shrink-0 p-1"
                  >
                    {isExpanded ? (
                      <ChevronUp className="h-4 w-4 text-gray-400" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-gray-400" />
                    )}
                  </button>
                </div>

                {/* Expanded detail — optional sets/reps entry */}
                {isExpanded && (
                  <ExerciseDetailExpanded
                    exercise={exercise}
                    isDone={isDone}
                    onComplete={handleComplete}
                    loading={completionLoading === exercise.id}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Archived */}
      {archivedExercises.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm">
          <button
            type="button"
            className="w-full flex items-center justify-between p-4 text-left"
            onClick={() => setShowArchived(!showArchived)}
          >
            <span className="text-sm font-medium text-gray-500">
              Archived ({archivedExercises.length})
            </span>
            {showArchived ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
          </button>
          {showArchived && (
            <div className="px-4 pb-4 space-y-2">
              {archivedExercises.map(exercise => (
                <div key={exercise.id} className="flex items-center gap-3 p-3 rounded-lg bg-gray-50">
                  <div className="h-10 w-10 rounded bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <Archive className="h-4 w-4 text-gray-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-500 truncate">{exercise.label}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ExerciseDetailExpanded({
  exercise,
  isDone,
  onComplete,
  loading
}: {
  exercise: StandaloneExercise;
  isDone: boolean;
  onComplete: (exercise: StandaloneExercise, sets?: number, reps?: string[]) => void;
  loading: boolean;
}) {
  const [sets, setSets] = useState('');
  const [reps, setReps] = useState('');
  const [showVideo, setShowVideo] = useState(false);
  const vj = exercise.value_json || {};

  return (
    <div className="px-4 pb-4 border-t border-gray-100 pt-3">
      {/* Full-width exercise gif */}
      {vj.gif_url && (
        <img src={vj.gif_url} alt={exercise.label} className="w-full rounded-lg mb-3" />
      )}

      {/* Video toggle */}
      {vj.video_url && (
        <div className="mb-3">
          <button
            type="button"
            onClick={() => setShowVideo(v => !v)}
            className="text-sm font-medium text-primary hover:text-primary/80 transition-colors"
          >
            {showVideo ? 'Hide Video' : 'Watch Video'}
          </button>
          {showVideo && (
            <div className="mt-2">
              {isEmbeddedVideo(vj.video_url) ? (
                <div className="relative aspect-video">
                  <iframe
                    src={toEmbedUrl(vj.video_url)}
                    title={`${exercise.label} video`}
                    className="absolute inset-0 w-full h-full rounded-lg"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              ) : (
                <video
                  key={vj.video_url}
                  src={vj.video_url}
                  controls
                  className="w-full rounded-lg"
                />
              )}
            </div>
          )}
        </div>
      )}

      {/* Formatted instructions */}
      {vj.instructions && (
        <div className="text-sm text-gray-600 mb-3">
          {vj.instructions.split('\n').map((line: string, index: number) => {
            const isBullet = line.trim().startsWith('-') || line.trim().startsWith('•');
            if (isBullet) {
              return (
                <li key={index} className="ml-4">
                  {line.replace(/^[-•]\s*/, '')}
                </li>
              );
            }
            return line.trim() ? <p key={index} className="mb-1">{line}</p> : null;
          })}
        </div>
      )}

      {!isDone && (
        <div className="flex items-end gap-3">
          <div>
            <label className="text-xs text-gray-500 block mb-1">Sets</label>
            <input
              type="number"
              value={sets}
              onChange={e => setSets(e.target.value)}
              placeholder={vj.sets || '—'}
              className="w-16 px-2 py-1.5 border rounded-md text-sm"
            />
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Reps</label>
            <input
              type="number"
              value={reps}
              onChange={e => setReps(e.target.value)}
              placeholder={vj.reps || '—'}
              className="w-16 px-2 py-1.5 border rounded-md text-sm"
            />
          </div>
          <button
            type="button"
            disabled={loading}
            onClick={() => {
              const setsVal = sets ? parseInt(sets) : undefined;
              const repsVal = reps ? [reps] : undefined;
              onComplete(exercise, setsVal, repsVal);
            }}
            className="px-4 py-1.5 bg-primary text-white text-sm rounded-md hover:bg-primary/90 disabled:opacity-50"
          >
            {loading ? 'Saving...' : 'Complete'}
          </button>
        </div>
      )}

      {isDone && (
        <p className="text-sm text-green-600 font-medium">Completed today</p>
      )}
    </div>
  );
}

export function ProgramsList() {
  const navigate = useNavigate();
  const { hasFeature, isExercisePlatform } = useFeatures();
  const hasStandaloneExercises = hasFeature('standalone_exercises');
  const hasPrograms = hasFeature('workout_plans') || hasFeature('rehab_programs');

  const [assignments, setAssignments] = useState<ProgramAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'active' | 'completed'>('active');
  const [planBuilding, setPlanBuilding] = useState<string | null>(null);
  const [planError, setPlanError] = useState<string | null>(null);

  // Empty-state actions for standalone patients: build a personalized plan or pick a starter routine
  const startPlan = async (routineKey?: 'full' | 'upper' | 'lower') => {
    setPlanBuilding(routineKey || 'generate');
    setPlanError(null);
    try {
      const result = routineKey
        ? await patientApiService.claimBaseRoutine(routineKey)
        : await patientApiService.generatePlan();
      if (result?.assignment_id) {
        navigate(`/programs/${result.assignment_id}`);
        return;
      }
      await fetchPrograms();
    } catch (error: any) {
      console.error('Failed to start plan:', error);
      setPlanError(error?.message || 'We could not build your plan. Please try again.');
    } finally {
      setPlanBuilding(null);
    }
  };
  const [stats, setStats] = useState<ProgramStats>({
    totalActive: 0,
    sessionsThisWeek: 0,
    currentStreak: 0,
    totalCompleted: 0
  });

  useEffect(() => {
    fetchPrograms();
  }, []);

  const fetchPrograms = async () => {
    setLoading(true);
    try {
      // Fetch all assignments
      const allPrograms = await patientApiService.getMyExercisePrograms();
      const programs = allPrograms.filter(p => p.assignment_type != "exercise_prescription" && p.assignment_type != "exercise_now");
      
      if (Array.isArray(programs)) {
        // Fetch additional details for each program
        const enrichedPrograms = await Promise.all(
          programs.map(async (assignment: any) => {
            try {
              // Get progress information
              const progress = await patientApiService.getAssignmentProgress(assignment.id).catch(() => null);
              
              // Get program details and schedule based on type
              let programDetails = {};
              let scheduleInfo: {
                scheduled_days: number[];
                frequency_per_week?: number;
                last_session_date?: string;
                next_session_date?: string;
              } = { scheduled_days: [] };
              
              if (assignment.assignment_type === 'workout_plan' && assignment.workout_plan_id) {
                // Fetch workout plan details
                const plan = await patientApiService.getWorkoutPlan(assignment.workout_plan_id).catch(() => null);
                if (plan) {
                  programDetails = {
                    name: plan.name,
                    description: plan.description,
                    duration_weeks: plan.duration_weeks,
                    fitness_level: plan.fitness_level
                  };
                }
                
                // Fetch current week's schedule to determine workout days
                const currentWeek = progress?.current_week || 1;
                try {
                  const weekSchedule = await patientApiService.getWorkoutWeekSchedule(
                    assignment.workout_plan_id,
                    currentWeek
                  );
                  
                  // Extract unique days from schedule (already in ISO format)
                  const scheduledDays = [...new Set(weekSchedule.map((s: any) => s.day_of_week))].sort() as number[];
                  scheduleInfo.scheduled_days = scheduledDays;
                  scheduleInfo.frequency_per_week = scheduledDays.length;
                } catch (error) {
                  console.error('Failed to fetch workout schedule:', error);
                  // Fallback to default MWF
                  scheduleInfo.scheduled_days = [1, 3, 5];
                  scheduleInfo.frequency_per_week = 3;
                }
                
              } else if (assignment.assignment_type === 'rehab_program' && assignment.rehab_program_id) {
                // Fetch rehab program details
                const program = await patientApiService.getRehabProgram(assignment.rehab_program_id).catch(() => null);
                if (program) {
                  programDetails = {
                    name: program.name,
                    description: program.description,
                    total_phases: program.total_phases,
                    condition: program.condition,
                    protocol_source: program.protocol_source
                  };
                }
                
                // Fetch current phase schedule
                const currentPhase = progress?.current_phase || 1;
                try {
                  const phaseSchedule = await patientApiService.getRehabPhaseSchedule(
                    assignment.rehab_program_id,
                    currentPhase
                  );
                  
                  if (phaseSchedule && phaseSchedule.length > 0) {
                    const frequency = phaseSchedule[0].frequency_per_week || 3;
                    scheduleInfo.frequency_per_week = frequency;
                    
                    // Map frequency to scheduled days (ISO format)
                    const dayMapping: { [key: number]: number[] } = {
                      1: [3], // Wednesday
                      2: [2, 4], // Tuesday, Thursday
                      3: [1, 3, 5], // Monday, Wednesday, Friday
                      4: [1, 2, 4, 5], // Mon, Tue, Thu, Fri
                      5: [1, 2, 3, 4, 5], // Weekdays
                      6: [1, 2, 3, 4, 5, 6], // Mon-Sat
                      7: [1, 2, 3, 4, 5, 6, 7] // Daily
                    };
                    scheduleInfo.scheduled_days = dayMapping[frequency] || [1, 3, 5];
                  } else {
                    // Fallback
                    scheduleInfo.scheduled_days = [1, 3, 5];
                    scheduleInfo.frequency_per_week = 3;
                  }
                } catch (error) {
                  console.error('Failed to fetch rehab schedule:', error);
                  scheduleInfo.scheduled_days = [1, 3, 5];
                  scheduleInfo.frequency_per_week = 3;
                }
                
              } else if (assignment.assignment_type === 'single_prescription' && assignment.exercise_prescription_id) {
                // Fetch prescription details
                const prescription = await patientApiService.getExerciseProgram(assignment.exercise_prescription_id).catch(() => null);
                if (prescription) {
                  programDetails = {
                    name: prescription.name,
                    description: prescription.description
                  };
                  
                  // Get frequency from prescription schedule
                  if (prescription.schedule?.rehab_frequency) {
                    const frequency = prescription.schedule.rehab_frequency.times_per_week || 3;
                    const frequency_int = parseInt(frequency.replace('x_week', ''), 10);
                    scheduleInfo.frequency_per_week = frequency_int;
                    
                    // Map frequency to days
                    const dayMapping: { [key: string]: number[] } = {
                      '1x_week': [3], // Wednesday
                      '2x_week': [2, 4], // Tuesday, Thursday
                      '3x_week': [1, 3, 5], // Monday, Wednesday, Friday
                      '4x_week': [1, 2, 4, 5],
                      '5x_week': [1, 2, 3, 4, 5],
                      '6x_week': [1, 2, 3, 4, 5, 6],
                      '7x_week': [0, 1, 2, 3, 4, 5, 6]
                    };
                    scheduleInfo.scheduled_days = dayMapping[frequency] || [1, 3, 5];
                  } else if(prescription.schedule?.recommended_frequency){
                    const frequency = prescription.schedule.recommended_frequency|| '3x_week';
                    const frequency_int = parseInt(frequency.replace('x_week', ''), 10);
                    scheduleInfo.frequency_per_week = frequency_int;
                    
                    // Map frequency to days
                    const dayMapping: { [key: string]: number[] } = {
                      '1x_week': [3], // Wednesday
                      '2x_week': [2, 4], // Tuesday, Thursday
                      '3x_week': [1, 3, 5], // Monday, Wednesday, Friday
                      '4x_week': [1, 2, 4, 5],
                      '5x_week': [1, 2, 3, 4, 5],
                      '6x_week': [1, 2, 3, 4, 5, 6],
                      '7x_week': [0, 1, 2, 3, 4, 5, 6]
                    };
                    scheduleInfo.scheduled_days = dayMapping[frequency] || [1, 3, 5];

                  }else {
                    // This needs to be corrected to handle single prescriptions for fitness. Should get schedule from exercise_prescription and map to days based on x per week frequency
                    scheduleInfo.scheduled_days = [1,2,3, 4,5];
                    scheduleInfo.frequency_per_week = 5;
                  }
                }
              }
              
              // Calculate next session date based on actual schedule
              scheduleInfo.next_session_date = calculateNextSessionDate(
                scheduleInfo.scheduled_days,
                progress?.last_completed_date
              );
              
              return {
                ...assignment,
                program_details: programDetails,
                progress: progress || calculateFallbackProgress(assignment),
                schedule_info: scheduleInfo
              };
            } catch (error) {
              console.error(`Error fetching details for assignment ${assignment.id}:`, error);
              return assignment;
            }
          })
        );
        
        setAssignments(enrichedPrograms);
        calculateStats(enrichedPrograms);
      }
    } catch (error) {
      console.error('Error fetching programs:', error);
    } finally {
      setLoading(false);
    }
  };


  const calculateFallbackProgress = (assignment: any) => {
    const startDate = parseISO(assignment.start_date);
    const today = new Date();
    const daysDiff = differenceInDays(today, startDate);
    const weeksDiff = Math.floor(daysDiff / 7);
    
    return {
      current_week: weeksDiff + 1,
      sessions_completed: 0,
      sessions_scheduled: 0,
      completion_percentage: 0
    };
  };

  const calculateNextSessionDate = (scheduledDaysISO: number[], lastCompletedDate?: string): string => {
    const today = startOfDay(new Date());
    const todayJSDay = today.getDay(); // 0=Sun, 1=Mon...6=Sat
    const todayISODay = jsToISODay(todayJSDay); // 1=Mon...7=Sun
    
    if (!scheduledDaysISO || scheduledDaysISO.length === 0) {
      // No schedule, default to tomorrow
      return format(addDays(today, 1), 'yyyy-MM-dd');
    }
    
    // Sort scheduled days to ensure proper order
    const sortedDays = [...scheduledDaysISO].sort((a, b) => a - b);
    
    // Check if we've already completed today's session
    let todayCompleted = false;
    if (lastCompletedDate) {
      const lastCompleted = typeof lastCompletedDate === 'string'
        ? parseISO(lastCompletedDate)
        : new Date(lastCompletedDate);
      todayCompleted = isToday(lastCompleted);
    }
    
    // If today is a scheduled day and not completed, return today
    if (sortedDays.includes(todayISODay) && !todayCompleted) {
      return format(today, 'yyyy-MM-dd');
    }
    
    // Find the next scheduled day after today
    let nextISODay = sortedDays.find(d => d > todayISODay);
    
    let daysUntilNext: number;
    if (nextISODay) {
      // Found a day later this week
      daysUntilNext = nextISODay - todayISODay;
    } else {
      // No more days this week, get first day of next week
      nextISODay = sortedDays[0];
      daysUntilNext = (7 - todayISODay) + nextISODay;
    }
    
    return format(addDays(today, daysUntilNext), 'yyyy-MM-dd');
  };

  const calculateStats = (programs: ProgramAssignment[]) => {
    const activePrograms = programs.filter(p => p.status === 'active');
    const completedPrograms = programs.filter(p => p.status === 'completed');
    
    // Calculate sessions this week based on actual schedules
    const sessionsThisWeek = activePrograms.reduce((sum, program) => {
      return sum + (program.schedule_info?.frequency_per_week || 3);
    }, 0);
    
    // Calculate streak (simplified - would need actual completion data)
    const streak = activePrograms.length > 0 ? 
      activePrograms[0].progress.streak_days : 0; // Placeholder to be improved, only includes one program
    
    setStats({
      totalActive: activePrograms.length,
      sessionsThisWeek,
      currentStreak: streak,
      totalCompleted: completedPrograms.length
    });
  };

  const filteredAssignments = assignments.filter(assignment => {
    if (filter === 'all') return true;
    if (filter === 'active') return assignment.status === 'active';
    if (filter === 'completed') return assignment.status === 'completed';
    return true;
  });

  const getProgramIcon = (type: string) => {
    switch (type) {
      case 'workout_plan': return TrendingUp;
      case 'rehab_program': return Activity;
      default: return Target;
    }
  };

  const getProgramTypeLabel = (type: string) => {
    switch (type) {
      case 'workout_plan': return 'Workout Plan';
      case 'rehab_program': return 'Rehab Program';
      case 'exercise_prescription': return 'Exercise Program';
      default: return 'Program';
    }
  };

  const getProgressLabel = (assignment: ProgramAssignment) => {
    if (assignment.assignment_type === 'workout_plan') {
      return `Week ${assignment.progress?.current_week || 1} of ${assignment.progress?.total_weeks || assignment.program_details?.duration_weeks || '?'}`;
    } else if (assignment.assignment_type === 'rehab_program') {
      return `Phase ${assignment.progress?.current_phase || 1} of ${assignment.progress?.total_phases || assignment.program_details?.total_phases || '?'}`;
    }
    return `${assignment.progress?.sessions_completed || 0} sessions completed`;
  };

  // Helper functions for day conversion
  const jsToISODay = (jsDay: number): number => jsDay === 0 ? 7 : jsDay;
  const isoDayToJS = (isoDay: number): number => isoDay === 7 ? 0 : isoDay;

  const getNextSessionDate = (assignment: ProgramAssignment) => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    
    // Find next scheduled day (assuming MWF for now)
    const scheduledDays = [1, 3, 5]; // Monday, Wednesday, Friday
    let nextDay = scheduledDays.find(d => d > dayOfWeek);
    
    if (!nextDay) {
      nextDay = scheduledDays[0]; // Next Monday
    }
    
    const daysUntilNext = nextDay > dayOfWeek ? nextDay - dayOfWeek : 7 - dayOfWeek + nextDay;
    return addDays(today, daysUntilNext);
  };

  // If patient has standalone exercises and no active program assignments, show exercises-only view
  const activeAssignments = assignments.filter(a => a.status === 'active');
  if (hasStandaloneExercises && activeAssignments.length === 0 && !loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <PageHeader title="My Exercises" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <StandaloneExercisesView />
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <PageHeader title="My Programs" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <PageHeader title="My Programs" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Active Programs</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{stats.totalActive}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg">
                <Activity className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">This Week</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{stats.sessionsThisWeek}</p>
                <p className="text-xs text-gray-500 mt-1">sessions scheduled</p>
              </div>
              <div className="p-3 bg-green-100 rounded-lg">
                <Calendar className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Current Streak</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{stats.currentStreak}</p>
                <p className="text-xs text-gray-500 mt-1">days</p>
              </div>
              <div className="p-3 bg-orange-100 rounded-lg">
                <Award className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Completed</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{stats.totalCompleted}</p>
                <p className="text-xs text-gray-500 mt-1">all time</p>
              </div>
              <div className="p-3 bg-purple-100 rounded-lg">
                <CheckCircle className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="bg-white rounded-lg shadow-sm p-1 mb-6 inline-flex">
          <button
            onClick={() => setFilter('active')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              filter === 'active' 
                ? 'bg-primary text-white' 
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Active
          </button>
          <button
            onClick={() => setFilter('completed')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              filter === 'completed' 
                ? 'bg-primary text-white' 
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Completed
          </button>
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              filter === 'all' 
                ? 'bg-primary text-white' 
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            All
          </button>
        </div>

        {/* Programs List */}
        {filteredAssignments.length === 0 ? (
          // Empty state - same as before
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <Activity className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {filter === 'active' && isExercisePlatform ? 'No active plan yet' : 'No programs found'}
            </h3>
            <p className="text-gray-600">
              {filter === 'active' && isExercisePlatform
                ? "Build a plan around your answers, or start with a simple routine."
                : filter === 'active'
                ? "You don't have any active programs at the moment."
                : filter === 'completed'
                ? "You haven't completed any programs yet."
                : "You don't have any programs assigned."}
            </p>
            {filter === 'active' && isExercisePlatform && (
              <div className="mt-6 max-w-sm mx-auto space-y-3">
                <button
                  onClick={() => startPlan()}
                  disabled={planBuilding !== null}
                  className="w-full px-4 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 disabled:opacity-50"
                >
                  {planBuilding === 'generate' ? 'Building your plan...' : 'Build my plan'}
                </button>
                <p className="text-xs text-gray-500">or start with a routine</p>
                <div className="grid grid-cols-3 gap-2">
                  {([['full', 'Full Body'], ['upper', 'Upper Body'], ['lower', 'Lower Body']] as const).map(([key, label]) => (
                    <button
                      key={key}
                      onClick={() => startPlan(key)}
                      disabled={planBuilding !== null}
                      className="px-3 py-2 text-sm bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                    >
                      {planBuilding === key ? '...' : label}
                    </button>
                  ))}
                </div>
                {planError && <p className="text-sm text-red-600">{planError}</p>}
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {filteredAssignments.map(assignment => {
              const Icon = getProgramIcon(assignment.assignment_type);
              const nextSessionDate = assignment.schedule_info?.next_session_date 
                ? parseISO(assignment.schedule_info.next_session_date)
                : new Date();
              const isNextSessionToday = isToday(nextSessionDate);
              
              return (
                <div
                  key={assignment.id}
                  className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => navigate(`/programs/${assignment.id}`)}
                >
                  <div className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-4">
                        <div className={`p-3 rounded-lg ${
                          assignment.assignment_type === 'workout_plan' 
                            ? 'bg-blue-100' 
                            : assignment.assignment_type === 'rehab_program'
                            ? 'bg-green-100'
                            : 'bg-teal-100'
                        }`}>
                          <Icon className={`h-6 w-6 ${
                            assignment.assignment_type === 'workout_plan' 
                              ? 'text-blue-600' 
                              : assignment.assignment_type === 'rehab_program'
                              ? 'text-green-600'
                              : 'text-teal-600'
                          }`} />
                        </div>
                        
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-1">
                            <h3 className="text-lg font-semibold text-gray-900">
                              {assignment.program_details?.name || 'Exercise Program'}
                            </h3>
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              assignment.status === 'active' 
                                ? 'bg-green-100 text-green-700'
                                : assignment.status === 'completed'
                                ? 'bg-gray-100 text-gray-700'
                                : 'bg-yellow-100 text-yellow-700'
                            }`}>
                              {assignment.status}
                            </span>
                          </div>
                          
                          <p className="text-sm text-gray-600 mb-2">
                            {getProgramTypeLabel(assignment.assignment_type)}
                            {assignment.schedule_info?.frequency_per_week && 
                              ` • ${assignment.schedule_info.frequency_per_week}x per week`}
                          </p>
                          
                          {assignment.program_details?.description && (
                            <p className="text-sm text-gray-500 mb-3 line-clamp-2">
                              {assignment.program_details.description}
                            </p>
                          )}
                          
                          <div className="flex flex-wrap items-center gap-4 text-sm">
                            <div className="flex items-center text-gray-600">
                              <BarChart className="h-4 w-4 mr-1" />
                              {getProgressLabel(assignment)}
                            </div>
                            
                            {assignment.status === 'active' && assignment.schedule_info?.next_session_date && (
                              <div className={`flex items-center ${
                                isNextSessionToday ? 'text-primary font-medium' : 'text-gray-600'
                              }`}>
                                <Clock className="h-4 w-4 mr-1" />
                                {isNextSessionToday 
                                  ? 'Session Today!' 
                                  : `Next: ${format(nextSessionDate, 'EEE, MMM d')}`}
                              </div>
                            )}
                            
                            {assignment.progress?.completion_percentage !== undefined && (
                              <div className="flex items-center text-gray-600">
                                <CheckCircle className="h-4 w-4 mr-1" />
                                {Math.round(assignment.progress.completion_percentage)}% complete
                              </div>
                            )}
                          </div>
                          
                          {/* Progress Bar */}
                          {assignment.status === 'active' && assignment.progress?.completion_percentage !== undefined && (
                            <div className="mt-3">
                              <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                                <div 
                                  className="bg-primary h-2 rounded-full transition-all"
                                  style={{ width: `${Math.min(assignment.progress.completion_percentage, 100)}%` }}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        {assignment.status === 'active' && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/programs/${assignment.id}/session/${format(new Date(), 'yyyy-MM-dd')}`);
                            }}
                            className={`p-2 rounded-lg transition-colors ${
                              isNextSessionToday
                                ? 'bg-primary text-white animate-pulse'
                                : 'bg-primary text-white hover:bg-primary/90'
                            }`}
                            title={isNextSessionToday ? "Start Today's Session" : "Start Session"}
                          >
                            <PlayCircle className="h-5 w-5" />
                          </button>
                        )}
                        <ChevronRight className="h-5 w-5 text-gray-400" />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Standalone exercises section — shown when patient has both programs and exercises */}
        {hasStandaloneExercises && (
          <div className="mt-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">My Exercises</h2>
            <StandaloneExercisesView />
          </div>
        )}
      </div>
    </div>
  );
}